<template>
  <div style="position: relative;padding-bottom: 50px;height:100%;">
    <div
      ref="messageContainer"
      id="scroll-target"
      style="height: 100%;overflow-y:scroll;"
      v-on:scroll="handleScroll"
    >
      <v-fade-transition>
        <div
          v-if="unreadBanner"
          class="unreadBanner"
        >
          <div>
            <v-icon
              left
              size="19"
              style="padding-left: 15px;"
            >
              mdi-eye
            </v-icon>
            <b>This contact has unread messages.</b>
          </div>
        </div>
      </v-fade-transition>
      <div
        v-if="loading"
        style="padding: 20px;text-align:center;font-size: 18px;color: rgba(0,0,0,0.15);"
      >
        Loading...
      </div>
      <div
        v-for="message in messages"
        :key="message.id"
      >
        <div v-if="message.type == 'Message from Contact'">
          <div class="talk-bubble tri-right">
            <div
              style="padding: 8px;padding-left: 15px;"
              :style="message.read ? 'background: white;' : 'background: #ffffa8;'"
            >
              <v-icon
                v-if="message.source == 'text'"
                style="padding-right: 5px;"
                small
              >
                mdi-phone-message
              </v-icon>
              <v-icon
                v-if="message.source == 'email'"
                style="padding-right: 5px;"
                small
              >
                mdi-email
              </v-icon>
              <b>{{ message.sent_by_name }}</b>&nbsp;<span
                style="color: rgba(0,0,0,0.5);font-size: 12px;">{{ convertToCalRef(message.sent_at) }}</span>
              <span v-if="checkPermission('contacts_messages_read', PERMISSIONS.CAN_EDIT)">
                <v-icon
                  v-if="!message.read"
                  style="float: right;"
                  @click="read(message)"
                >
                  mdi-eye
                </v-icon>
                <v-icon
                  v-else
                  style="opacity: 0.2;float: right;"
                  @click="read(message, false)"
                >
                  mdi-eye-off-outline
                </v-icon>
              </span>
            </div>
            <div class="tri-right">
              <div class="talktext">
                <div
                  v-if="message.subject"
                  style="border-bottom: 1px solid rgba(0,0,0,0.12);"
                >
                  <span style="font-size: 14px;font-weight:bold;">{{ message.subject }}</span>
                </div>
                <div
                  class="message-text"
                  style="overflow:hidden;padding-top:0px;white-space:pre-wrap;"
                  v-html="message.message"
                />
              </div>
            </div>
            <div
              v-if="message.attachments && message.attachments.length > 0"
              style="padding: 10px; border-top: 1px solid rgba(0,0,0,0.05);"
            >
              <div
                v-for="attachment in message.attachments"
                :key="attachment.id"
                style="display: flex; align-items: center; margin-top: 5px;"
              >
                <v-icon
                  small
                  class="mr-2"
                >
                  {{ getAttachmentIcon(attachment.content_type) }}
                </v-icon>
                <a
                  href="#"
                  @click.prevent="openAttachment(message.id, attachment)"
                  style="text-decoration: none; color: #1976D2; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: inline-block;"
                >
                  {{ attachment.original_name }}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <div style="margin-right: 30px;margin-top: 10px;text-align:right;display: relative;">
            <span
              style="color: rgba(0,0,0,0.5);font-size: 12px;">{{ convertToCalRef(message.sent_at) }}</span>&nbsp;<b>{{ message.sent_by_name }}</b>
          </div>
          <div style="display: flex;margin-left: 25px;flex-direction: column;">
            <div
              class="talk-bubble tri-right right-top internal"
              style="margin-left: auto;"
            >
              <div style="position: absolute;left:0;top: -22px;">
                <v-tooltip
                  v-if="message.destinations && message.destinations.includes('SMS')"
                  bottom
                  style="display:inline-block"
                >
                  <div slot="activator">
                    <v-icon size="18">
                      mdi-phone-message
                    </v-icon>
                  </div>
                  <span>Sent via SMS</span>
                </v-tooltip>
                <v-tooltip
                  v-if="message.destinations && message.destinations.includes('Email')"
                  bottom
                  style="display:inline-block"
                >
                  <div slot="activator">
                    <v-icon size="18">
                      mdi-email
                    </v-icon>
                  </div>
                  <span>Sent via Email</span>
                </v-tooltip>
                <v-tooltip
                  v-if="message.destinations && message.destinations.includes('Push')"
                  bottom
                  style="display:inline-block"
                >
                  <div slot="activator">
                    <v-icon size="18">
                      mdi-cellphone
                    </v-icon>
                  </div>
                  <span>Sent App Push Notification</span>
                </v-tooltip>
                <div v-if="message.certified">
                  <v-icon
                    size="18"
                    color="#262626"
                  >
                    mdi-seal
                  </v-icon>
                  <span style="font-weight: 700;text-transform: uppercase;color: #262626;">
                    Certified
                  </span>
                </div>
              </div>
              <div class="talktext">
                <div
                  v-if="message.subject"
                  style="border-bottom: 1px solid rgba(0,0,0,0.12);"
                >
                  <span style="font-size: 14px;font-weight:bold;">{{ message.subject }}</span>
                </div>
                <div
                  class="message-text"
                  style="overflow:hidden;padding-top:0px;white-space:pre-wrap;"
                  v-html="message.message"
                />
              </div>
            </div>
            <div
              v-if="message.certified"
              style="align-self: flex-end;padding-right: 35px;margin-bottom: 10px;"
            >
              <div v-if="message.read">
                First read by {{ contact.full_name }} {{ convertToCalRef(getEarliestReadDate(message.read_receipts)) }}
                -
                <a
                  style="color: rgba(0,0,0,0.7);text-decoration: underline;"
                  @click="focusedCertifiedMessage = message; viewCertifiedMessageReadReceipts = true"
                >
                  View Read Receipts
                </a>
                <v-icon
                  size="16"
                  style="padding-left: 10px;"
                >
                  mdi-eye
                </v-icon>
              </div>
              <div v-else>
                Unread
                <v-icon
                  size="16"
                  style="padding-left: 10px;"
                >
                  mdi-eye-off-outline
                </v-icon>
              </div>
            </div>
            <div
              v-else
              style="margin-bottom: 10px;"
            >

            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="checkPermission('contacts_messages', PERMISSIONS.CAN_CREATE)"
      style="position: absolute; bottom:0;left:0;right:0;background: white;border-top: 1px solid rgba(0,0,0,0.12);z-index: 99;"
    >
      <v-text-field
        v-if="!displayMessageEditor"
        v-model="direct_message.subject"
        hide-details
        :label="disabledMessages ? 'Enable Contact or add an email to send messages' : 'Send a message to ' + contact.first_name"
        :disabled="disabledMessages"
        :solo="!displayMessageEditor"
        @focus="displayMessageEditor = true"
      />
    </div>
    <v-dialog
      v-model="limit_text"
      persistent
      width="400"
    >
      <v-card>
        <v-card-text>
          <b>Warning</b>: Your message is over 300 characters. The text notification will be truncated.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="limit_text = false">
            Go Back
          </v-btn>
          <v-btn
            color="primary"
            dark
            @click="sendMessage(); limit_text = false; sending = true"
          >
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="displayMessageEditor"
      persistent
      width="800"
    >
      <v-card>
        <v-toolbar
          dense
          color="primary"
          dark
        >
          Send a message to {{ contact.full_name }}
        </v-toolbar>
        <v-card-text style="max-height: 60vh;overflow-y:auto;">
          <v-text-field
            v-model="direct_message.subject"
            hide-details
            label="Subject (optional)"
          />
          <br />
          Message:
          <Editor
            enable_formatting="true"
            ref="htmlEditor"
            style="background: white;color: black;"
          />
          <div v-if="domainInfo.text_enabled">
            <small>Messages over 300 characters will be truncated for text notifications, and will not contain embedded
              links or rich formatting options</small>
          </div>
          <div>
            <v-switch
              v-model="direct_message.certified"
              label="Certified Message"
              :hint="`Require ${contact.full_name} to log in to ${app_name} to view the message, and track when they read it.`"
              persistent-hint
            ></v-switch>
          </div>
          <div>
            <div style="font-size: 16px;font-weight: 700;margin-top: 20px;">
              Message Channels
            </div>
            <div style="display: flex;flex-direction: row;flex-wrap: wrap;">
              <v-checkbox
                v-model="direct_message.send_email"
                label="E-mail"
                :hint="contact.email ? contact.email : 'Contact does not have an email address'"
                :disabled="!contact.email || contact.notify_by_email == false"
                persistent-hint
                class="channel-switch"
              />
              <v-checkbox
                v-model="direct_message.send_sms"
                label="SMS"
                :hint="contact.phone ? contact.phone : 'Contact does not have a phone number'"
                :disabled="!contact.phone || contact.notify_by_phone == false"
                persistent-hint
                class="channel-switch"
              />
              <v-checkbox
                v-model="direct_message.send_push"
                label="Push Notification"
                :hint="contact.mobile_device_count ? 'Push Notifications Subscribed' : 'Contact has not installed the mobile app'"
                :disabled="contact.mobile_device_count == 0"
                persistent-hint
                class="channel-switch"
              />
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            flat
            @click="displayMessageEditor = false"
          >
            Cancel
          </v-btn>
          <v-btn
            :disabled="sending"
            color="blue"
            dark
            @click="sendMessage()"
          >
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="viewCertifiedMessageReadReceipts"
      persistent
      width="800"
    >
      <v-card v-if="focusedCertifiedMessage">
        <v-toolbar
          dense
          color="primary"
          dark
        >
          <v-icon>
            mdi-seal
          </v-icon>
          &nbsp;Read Receipts | {{ focusedCertifiedMessage.subject }}
        </v-toolbar>
        <v-card-text style="max-height: 60vh;overflow-y: scroll;">
          <div>
            <div
              v-for="receipt in focusedCertifiedMessage.read_receipts"
              :key="receipt.id"
            >
              <v-card>
                <v-card-text style="display: flex;">
                  <div>
                    <div
                      v-if="receipt.view_method == 'open'"
                      style="font-weight:700;font-size: 16px;"
                    >
                      Explicitly Opened Certified Message
                    </div>
                    <div
                      v-if="receipt.view_method == 'view'"
                      style="font-weight:700;font-size: 16px;"
                    >
                      Viewed Message in Message History
                    </div>
                    <div>
                      <b>Read At: </b> {{ convertToLocal(receipt.read_at) }}
                    </div>
                    <div>
                      <b>IP Address: </b> {{ receipt.ip_addr }}
                    </div>
                    <div>
                      <b>User Agent: </b> {{ receipt.user_agent }}
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="viewCertifiedMessageReadReceipts = false; focusedCertifiedMessage = null"
            dark
            color="primary"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="displayAttachmentPreview"
      max-width="800"
    >
      <v-card>
        <v-card-text v-if="focusedAttachment">
          <div style="display: flex;justify-content: center;flex-direction: column;align-items: center;">
            <img
              v-if="getAttachmentType(focusedAttachment.content_type) == 'image'"
              :src="getAttachmentPreview(focusedAttachment.message_id, focusedAttachment.attachment_id)"
              style="max-width: 100%;max-height: 60vh;object-fit: contain;"
            />
            <video
              v-else-if="getAttachmentType(focusedAttachment.content_type) == 'video'"
              :src="getAttachmentPreview(focusedAttachment.message_id, focusedAttachment.attachment_id)"
              style="max-width: 100%;max-height: 60vh;"
              controls
            />
            <audio
              v-else-if="getAttachmentType(focusedAttachment.content_type) == 'audio'"
              :src="getAttachmentPreview(focusedAttachment.message_id, focusedAttachment.attachment_id)"
              style="width: 100%;"
              controls
            />
            <div
              v-else-if="getAttachmentType(focusedAttachment.content_type) == 'pdf'"
              style="width: 100%;display: flex;flex-direction: column;align-items: center;"
            >
              <iframe
                :src="getAttachmentPreview(focusedAttachment.message_id, focusedAttachment.attachment_id)"
                style="width: 100%;height: 60vh;border: 1px solid #ccc;"
              />
              <div style="margin-top: 10px;">
                <a
                  :href="getAttachmentPreview(focusedAttachment.message_id, focusedAttachment.attachment_id)"
                  target="_blank"
                  style="color: #1976D2;text-decoration: none;"
                >
                  <v-icon small>mdi-download</v-icon> Download PDF
                </a>
              </div>
            </div>
            <div v-else>
              <b>Unsupported Attachment Type</b>
              <br />
              File type of {{ focusedAttachment.content_type }} cannot be previewed.
              <br />
              <a
                :href="getAttachmentPreview(focusedAttachment.message_id, focusedAttachment.attachment_id)"
                target="_blank"
                style="color: #1976D2;text-decoration: none;"
              >
                <v-icon small>mdi-download</v-icon> Download File
              </a>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            v-if="focusedAttachment && !focusedAttachment.moved && checkPermission('contacts_files', PERMISSIONS.CAN_CREATE)"
            @click="promptMoveFile()"
            color="primary"
            flat
          >
            Move to Contact Files
          </v-btn>
          <v-btn
            @click="displayAttachmentPreview = false"
            color="primary"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="displayMoveDialog"
      max-width="500"
    >
      <v-card>
        <v-toolbar
          dense
          color="primary"
          dark
        >
          Move to Contact Files
        </v-toolbar>
        <v-card-text>
          Please provide a name for this file in the contact's files:
          <v-text-field
            v-model="newAttachmentName"
            label="File Name"
            autofocus
            @keyup.enter="moveFile()"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="displayMoveDialog = false"
            flat
          >
            Cancel
          </v-btn>
          <v-btn
            @click="moveFile()"
            color="primary"
            :loading="movingAttachment"
          >
            Move File
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api'
import Editor from '@/components/admin/Editor'
import { mapGetters } from 'vuex'

export default {
  name: 'ContactMessages',
  props: ['contact'],
  components: {
    Editor
  },
  data() {
    return {
      messages: [],
      page: 1,
      count: 0,
      max_pages: 0,
      unread: 0,
      loading: false,
      displayMessageEditor: false,
      direct_message: {
        certified: false,
        send_email: this.contact.notify_by_email && !!this.contact.email,
        send_sms: this.contact.notify_by_phone && !!this.contact.phone,
        send_push: this.contact.mobile_device_count > 0
      },
      scroll_down: true,
      limit_text: false,
      sending: false,
      viewCertifiedMessageReadReceipts: false,
      focusedCertifiedMessage: null,
      displayAttachmentPreview: false,
      focusedAttachment: null,
      displayMoveDialog: false,
      newAttachmentName: '',
      movingAttachment: false,
    }
  },
  watch: {
    contactId() {
      this.messages = []
      this.loadNotifications()
    }
  },
  computed: {
    ...mapGetters('auth', [
      'getDomainInfo',
    ]),
    noMoreMessages() {
      if (this.count < 5) {
        return true
      }
      if (this.page === this.max_pages) {
        return true
      }
      return false
    },
    disabledMessages() {
      return !this.contact.is_active || !this.contact.email
    },
    unreadBanner() {
      var checkMessages = false
      this.messages.forEach((message) => {
        if (!message.read && message.type == "Message from Contact")
          checkMessages = true
      })
      return checkMessages
    },
    contactId() {
      return this.contact.id
    }
  },
  created() {
    this.loadNotifications()
    this.domainInfo = this.getDomainInfo
  },
  methods: {
    openAttachment(message_id, attachment) {
      this.focusedAttachment = {
        message_id: message_id,
        attachment_id: attachment.id,
        content_type: attachment.content_type,
        original_name: attachment.original_name,
        moved: attachment.moved
      }
      this.displayAttachmentPreview = true
    },
    promptMoveFile() {
      this.newAttachmentName = this.focusedAttachment.original_name
      this.displayMoveDialog = true
    },
    moveFile() {
      this.movingAttachment = true
      let finalName = this.newAttachmentName
      let originalExt = ''
      if (this.focusedAttachment.original_name.includes('.')) {
        originalExt = '.' + this.focusedAttachment.original_name.split('.').pop()
      }
      if (originalExt && !finalName.endsWith(originalExt)) {
        finalName += originalExt
      }
      api.userform.contactNotifications.moveAttachment(this.contact.id, this.focusedAttachment.message_id, this.focusedAttachment.attachment_id, finalName)
        .then(() => {
          this.displayMoveDialog = false
          this.displayAttachmentPreview = false
          this.messages.forEach(message => {
            if (message.id === this.focusedAttachment.message_id) {
              message.attachments.forEach(attachment => {
                if (attachment.id === this.focusedAttachment.attachment_id) {
                  attachment.moved = true
                  attachment.original_name = finalName
                }
              })
            }
          })
        })
        .finally(() => {
          this.movingAttachment = false
        })
    },
    getAttachmentPreview(message_id, attachment_id) {
      return api.userform.contactNotifications.getAttachmentPreview(this.contact.id, message_id, attachment_id)
    },
    getAttachmentType(content_type) {
      if (content_type.startsWith('image/')) {
        return 'image'
      } else if (content_type.startsWith('video/')) {
        return 'video'
      } else if (content_type.startsWith('audio/')) {
        return 'audio'
      } else if (content_type.startsWith('application/pdf')) {
        return 'pdf'
      } else {
        return 'file'
      }
    },
    getAttachmentIcon(content_type) {
      if (content_type.startsWith('image/')) {
        return 'mdi-image'
      } else if (content_type.startsWith('video/')) {
        return 'mdi-video'
      } else if (content_type.startsWith('audio/')) {
        return 'mdi-music-note'
      } else if (content_type.startsWith('application/pdf')) {
        return 'mdi-file-pdf-box'
      } else if (content_type.startsWith('application/msword') || content_type.startsWith('application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
        return 'mdi-file-word'
      } else if (content_type.startsWith('application/vnd.ms-excel') || content_type.startsWith('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
        return 'mdi-file-excel'
      } else if (content_type.startsWith('application/vnd.ms-powerpoint') || content_type.startsWith('application/vnd.openxmlformats-officedocument.presentationml.presentation')) {
        return 'mdi-file-powerpoint'
      } else {
        return 'mdi-file'
      }
    },
    handleScroll(e) {
      if (e.target.scrollTop < 250 && !this.loading && !this.noMoreMessages) {
        this.page++
        this.loadNotifications()
      }
    },
    getEarliestReadDate(arr) {
      return arr.reduce((earliest, current) => {
        const currentTimestamp = new Date(current.read_at).getTime();
        return currentTimestamp < earliest ? currentTimestamp : earliest;
      }, Infinity);
    },
    loadNotifications() {
      this.loading = true
      api.userform.contactNotifications.get(this.contact, { pageNum: this.page, count: 10 })
        .then(data => {
          data.results.forEach((message) => {
            message.expand = false
            this.messages.unshift(message)
          })
          this.loading = false
          this.count = data.count
          this.page = data.page_number
          this.max_pages = data.num_pages
        })
        .finally(() => {
          if (this.scroll_down) {
            this.$refs.messageContainer.scrollTop = Math.floor(this.$refs.messageContainer.scrollHeight)
            this.scroll_down = false
          }
        })
    },
    sendMessage() {
      this.loading = true
      var temp = this.$refs.htmlEditor.getHTML()
      if (temp.replace(/(<([^>]+)>)/ig, '').length > 300 && !this.limit_text && this.domainInfo.text_enabled) {
        this.limit_text = true
        this.sending = false
      } else {
        api.userform.contact.sendMessage(this.contact, {
          'subject': this.direct_message.subject,
          'body': this.$refs.htmlEditor.getHTML(),
          'certified': this.direct_message.certified,
          'send_email': this.direct_message.send_email,
          'send_sms': this.direct_message.send_sms,
          'send_push': this.direct_message.send_push
        })
          .then(() => {
            this.messages = []
            this.page = 1
            this.loading = false
            this.scroll_down = true
            this.direct_message = {
              certified: false,
              send_email: this.contact.notify_by_email && !!this.contact.email,
              send_sms: this.contact.notify_by_phone && !!this.contact.phone,
              send_push: this.contact.mobile_device_count > 0
            }
            this.sending = false
            this.displayMessageEditor = false
            this.loadNotifications()
          })
      }
    },
    read(item, read = true) {
      api.userform.contactNotifications.read(this.contact, item, read)
        .then(() => {
          item.read = read
        })
    }
  }
}
</script>

<style>
/* CSS talk bubble */
.talk-bubble {
  margin: 0px 25px 0 0;
  display: inline-block;
  position: relative;
  height: auto;
  background: rgb(242, 242, 242);
  min-width: 400px;
  max-width: 800px;
}

.internal {
  background: rgb(222, 238, 255);
}

.tri-right.left-top:after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: -20px;
  right: auto;
  top: 0px;
  bottom: auto;
  border: 22px solid;
  border-color: rgb(242, 242, 242) transparent transparent transparent;
}

.tri-right.right-top:after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: auto;
  right: -20px;
  top: 0px;
  bottom: auto;
  border: 20px solid;
  border-color: rgb(222, 238, 255) transparent transparent transparent;
}

/* talk bubble contents */
.talktext {
  padding: 1em;
  text-align: left;
  line-height: 1.5em;
}

.talktext p {
  /* remove webkit p margins */
  -webkit-margin-before: 0em;
  -webkit-margin-after: 0em;
}


/* MESSAGES ^ */

.message-text {
  position: relative;
}

.unreadBanner {
  position: absolute;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  top: 0;
  left: 0;
  right: 0;
  background: #ffff72;
  color: black;
  width: 100%;
  z-index: 1;
  padding: 10px;
}

.unreadBanner:hover {
  top: -42px;
}

.channel-switch {
  margin-top: 0 !important;
}
</style>
